import { Link } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { getLiveTag, getLiveTags } from '@/hooks/useLiveStreams';
import { formatTimeAgo } from '@/hooks/useVideoFeed';

interface LiveStreamCardProps {
  event: NostrEvent;
  fullWidth?: boolean;
}

export function LiveStreamCard({ event, fullWidth = false }: LiveStreamCardProps) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;

  const title = getLiveTag(event, 'title') ?? 'Untitled Stream';
  const image = getLiveTag(event, 'image');
  const viewers = getLiveTag(event, 'current_participants');
  const tags = getLiveTags(event, 't');
  const d = getLiveTag(event, 'd') ?? '';
  const timeAgo = formatTimeAgo(event.created_at);
  const displayName = metadata?.name ?? metadata?.display_name ?? event.pubkey.slice(0, 8) + '…';

  const href = `/live/${event.pubkey}/${d}`;

  return (
    <div className="group" style={fullWidth ? { width: '100%' } : { width: 280, flexShrink: 0 }}>
      {/* Thumbnail */}
      <Link
        to={href}
        className="relative block rounded-xl overflow-hidden"
        style={{ paddingBottom: '56.25%', background: 'var(--bg-secondary)', display: 'block' }}
      >
        {image ? (
          <img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          /* Gradient placeholder */
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #1a3a1a 0%, #0a1a0a 100%)' }}
          >
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--gb-accent)', opacity: 0.5 }}>
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
            </svg>
          </div>
        )}

        {/* LIVE badge */}
        <span
          className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold text-white"
          style={{ background: '#f44336' }}
        >
          <span
            className="inline-block rounded-full"
            style={{ width: 6, height: 6, background: '#fff', animation: 'pulse 1.5s ease-in-out infinite' }}
          />
          LIVE
        </span>

        {/* Viewer count */}
        {viewers && (
          <span
            className="absolute bottom-2 left-2 flex items-center gap-1 text-xs px-1.5 py-0.5 rounded text-white"
            style={{ background: 'rgba(0,0,0,0.75)' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
            </svg>
            {Number(viewers).toLocaleString()}
          </span>
        )}

        {/* Play overlay on hover */}
        <div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: 'rgba(0,0,0,0.3)' }}
        >
          <div className="bg-white/90 rounded-full p-3">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="translate-x-0.5 text-black">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </Link>

      {/* Info row */}
      <div className="flex gap-2 pt-2">
        <Link to={`/profile/${event.pubkey}`} className="shrink-0">
          <Avatar className="h-9 w-9">
            <AvatarImage src={metadata?.picture} />
            <AvatarFallback
              className="text-xs font-bold"
              style={{ background: 'var(--gb-accent)', color: '#fff' }}
            >
              {displayName[0]?.toUpperCase() ?? '?'}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <Link
            to={href}
            className="block text-sm font-semibold leading-snug no-underline hover:opacity-80 transition-opacity line-clamp-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {title}
          </Link>
          <Link
            to={`/profile/${event.pubkey}`}
            className="text-xs no-underline hover:underline truncate block mt-0.5"
            style={{ color: 'var(--text-secondary)' }}
          >
            {displayName}
          </Link>
          {tags.length > 0 && (
            <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--gb-accent)' }}>
              {tags.slice(0, 3).map(t => `#${t}`).join(' ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function LiveStreamCardSkeleton() {
  return (
    <div className="shrink-0" style={{ width: 280 }}>
      <Skeleton
        className="w-full rounded-xl"
        style={{ paddingBottom: '56.25%', background: 'var(--bg-secondary)' }}
      />
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-9 w-9 rounded-full shrink-0" style={{ background: 'var(--bg-secondary)' }} />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-full" style={{ background: 'var(--bg-secondary)' }} />
          <Skeleton className="h-3 w-1/2" style={{ background: 'var(--bg-secondary)' }} />
        </div>
      </div>
    </div>
  );
}
